import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { TerminalImportLogEntity } from "src/app/features/TerminalImportLog/types/TerminalImportLogEntity";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const TerminalImportLogsTable: FC<ResourceTableProps<TerminalImportLogEntity>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<TerminalImportLogEntity>): ExtendedColumnsType<TerminalImportLogEntity>[] => {
            return [
                {
                    title: t("terminalImportLogList.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("terminalImportLogList.fileName"),
                    key: "fileName",
                    dataIndex: "fileName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "fileName" ? sortInfo.order : null,
                },
                {
                    title: t("terminalImportLogList.importSuccessful"),
                    key: "importSuccessful",
                    dataIndex: "importSuccessful",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "importSuccessful" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("terminalImportLogList.importedRecords"),
                    key: "importedRecords",
                    dataIndex: "importedRecords",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "importedRecords" ? sortInfo.order : null,
                },
                {
                    title: t("terminalImportLogList.importDate"),
                    key: "importDate",
                    dataIndex: "importDate",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "importDate" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: TerminalImportLogEntity) =>
        navigate(`${BASE_PATH}/${ResourceName.TERMINAL_IMPORT_LOG}/${FORM_ACTION.VIEW}/${record.id}`);

    return (
        (datasource && (
            <GenericTable<TerminalImportLogEntity>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="terminalImportLogs_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: TerminalImportLogEntity) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
            />
        )) ||
        null
    );
};
